import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Instagram, Facebook, Search, Phone, Mail, Info, User } from "lucide-react";
import api from "@/lib/api";
import { useSettings, DEFAULT_CONTACT } from "@/components/shared";
import SearchOverlay from "@/components/layout/SearchOverlay";

export const NAV_LINKS = [
  { to: "/", label: "Accueil" },
  { to: "/notre-histoire", label: "Notre Histoire" },
  { to: "/carte", label: "Carte" },
  { to: "/le-bar", label: "Le Bar" },
  { to: "/evenements", label: "Événements" },
  { to: "/galerie", label: "Galerie" },
  { to: "/fidelite", label: "Fidélité" },
  { to: "/contact", label: "Contact" },
];

export const Monogram = ({ className = "" }) => (
  <span
    className={`flex items-center justify-center rounded-md border border-gold/40 text-[11px] font-bold tracking-[0.15em] text-gold ${className}`}
  >
    PM
  </span>
);

export default function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const contact = { ...DEFAULT_CONTACT, ...(settings?.contact || {}) };
  const [navItems, setNavItems] = useState(NAV_LINKS);

  const headerCta = location.pathname.startsWith("/services")
    ? { to: "/services#devis", label: "Demander un devis", testId: "header-quote-button" }
    : location.pathname.startsWith("/comptoir")
    ? { to: "/comptoir#boutique", label: "Découvrir la boutique", testId: "header-shop-button" }
    : { to: "/reservation", label: "Réserver", testId: "header-reserve-button" };

  useEffect(() => {
    api
      .get("/navigation")
      .then((r) => {
        if (r.data.length) {
          setNavItems(r.data.map((item) => ({ to: item.path, label: item.label })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-[var(--banner-h,0px)] z-50 border-b border-white/10 bg-black/60 backdrop-blur-xl"
      >
        {/* Rangée utilitaire — desktop */}
        <div className="hidden h-9 items-center justify-between border-b border-white/5 px-10 lg:flex" data-testid="header-utility-bar">
          <div className="flex items-center gap-6 text-[11px] tracking-wide text-ivory/45">
            <a href={`tel:${(contact.whatsapp || "").replace(/\s/g, "")}`} className="flex items-center gap-2 transition-colors hover:text-gold" data-testid="header-phone">
              <Phone className="h-3.5 w-3.5 text-gold/70" strokeWidth={1.5} />
              {contact.phone_display}
            </a>
            <span className="text-ivory/35" data-testid="header-address">
              {contact.address_street}, {contact.address_city}
            </span>
          </div>
          <div className="flex items-center gap-5 text-[11px] font-medium uppercase tracking-[0.12em]">
            <Link to="/notre-histoire" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="header-about-link">
              <Info className="h-3.5 w-3.5" strokeWidth={1.5} />
              À propos
            </Link>
            <Link to="/contact" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="header-contact-link">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
              Contact
            </Link>
            <Link to="/mon-espace" className="flex items-center gap-1.5 text-ivory/55 transition-colors hover:text-gold" data-testid="header-account-link">
              <User className="h-3.5 w-3.5" strokeWidth={1.5} />
              Compte
            </Link>
            <span className="h-3.5 w-px bg-white/10" />
            <a
              href={settings?.instagram_url || "https://instagram.com"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="header-instagram"
              className="text-ivory/55 transition-colors hover:text-gold"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
            </a>
            <a
              href={settings?.facebook_url || "https://facebook.com"}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="header-facebook"
              className="text-ivory/55 transition-colors hover:text-gold"
            >
              <Facebook className="h-4 w-4" strokeWidth={1.5} />
            </a>
          </div>
        </div>

        {/* Rangée principale */}
        <div className="flex h-20 w-full items-center gap-4 px-5 md:h-24 md:gap-8 md:px-10">
          <Link to="/palais" className="flex min-w-0 shrink items-center gap-3 md:gap-4" data-testid="header-brand">
            {settings?.logo && (
              <img
                src={settings.logo}
                alt="Logo Palais Maasaï"
                className="h-10 w-auto md:h-12"
                data-testid="header-logo"
              />
            )}
            <span className="truncate text-xs font-bold tracking-[0.18em] text-ivory transition-colors hover:text-gold sm:text-sm md:text-base md:tracking-[0.22em] lg:text-lg lg:tracking-[0.28em]">
              PALAIS&nbsp;MAASAÏ
            </span>
          </Link>

          {/* Champ de recherche central bien visible */}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="header-search-field"
            className="mx-auto hidden h-12 w-full max-w-xs items-center gap-3 rounded-full border border-white/15 bg-white/[0.04] pl-5 pr-2 text-sm text-ivory/40 transition-colors hover:border-gold/40 hover:text-ivory/60 md:flex lg:max-w-xl"
          >
            <span className="flex-1 text-left">Rechercher un plat, une prestation, un produit…</span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-copper text-ivory">
              <Search className="h-4 w-4" strokeWidth={1.5} />
            </span>
          </button>

          <div className="ml-auto flex shrink-0 items-center gap-3 md:ml-0">
            <button
              data-testid="header-search-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Rechercher"
              className="flex h-11 w-11 items-center justify-center rounded-md border border-white/10 text-ivory transition-colors hover:border-gold hover:text-gold md:hidden"
            >
              <Search className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <Link
              to="/mon-espace"
              aria-label="Mon compte"
              data-testid="header-account-icon"
              className="hidden h-11 w-11 items-center justify-center rounded-md border border-white/10 text-ivory transition-colors hover:border-gold hover:text-gold md:flex lg:hidden"
            >
              <User className="h-5 w-5" strokeWidth={1.5} />
            </Link>
            {headerCta && (
              <Link to={headerCta.to} data-testid={headerCta.testId} className="btn-primary !h-11 !px-5 md:!px-8">
                {headerCta.label}
              </Link>
            )}
            <button
              data-testid="nav-menu-button"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              className="flex h-11 w-11 items-center justify-center rounded-md border border-white/10 text-ivory transition-colors hover:border-gold hover:text-gold lg:hidden"
            >
              {open ? <X className="h-5 w-5" strokeWidth={1.5} /> : <Menu className="h-5 w-5" strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {/* Rangée navigation — desktop, centrée, sans débordement */}
        <nav className="hidden flex-wrap items-center justify-center gap-x-5 gap-y-1 border-t border-white/5 px-8 py-2.5 lg:flex" data-testid="header-nav">
          {navItems.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`header-link-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap border-b-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  isActive
                    ? "border-copper text-gold"
                    : "border-transparent text-ivory/60 hover:text-gold"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="fixed inset-0 z-40 flex flex-col bg-ink/[0.985] backdrop-blur-2xl"
          >
            <nav className="flex flex-1 flex-col justify-center px-8 md:px-20">
              {navItems.map((link, i) => (
                <motion.div
                  key={link.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={link.to}
                    data-testid={`nav-link-${link.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className="group flex items-baseline gap-5 border-b border-white/5 py-4 md:py-5"
                  >
                    <span className="text-xs font-semibold tracking-[0.2em] text-copper">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="text-2xl font-bold tracking-tight text-ivory/80 transition-colors duration-300 group-hover:text-gold sm:text-3xl md:text-5xl">
                      {link.label}
                    </span>
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="flex flex-wrap items-center justify-between gap-4 px-8 pb-10 md:px-20"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-ivory/40">
                {contact.address_street}, {contact.address_city} — WhatsApp {contact.phone_display}
              </p>
              <div className="flex items-center gap-3">
                <a
                  href={settings?.instagram_url || "https://instagram.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="nav-instagram"
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                >
                  <Instagram className="h-4 w-4" strokeWidth={1.5} />
                </a>
                <a
                  href={settings?.facebook_url || "https://facebook.com"}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="nav-facebook"
                  className="flex h-10 w-10 items-center justify-center rounded-md border border-white/10 text-ivory/60 transition-colors hover:border-gold hover:text-gold"
                >
                  <Facebook className="h-4 w-4" strokeWidth={1.5} />
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
